import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, TrendingDown, Wallet, ClipboardList, Users, Sparkles, CheckCircle2, Loader2 } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Brand from "@/components/Brand";

// Family CFO Plan — public landing page for lead generation. Sells the outcome
// (household money plan, debt reduction, monthly accountability), not the
// machinery. Two CTAs: "Start intake" (self-serve) + "Enquire" (qualification).
export default function FamilyCfoLanding() {
    const [enquire, setEnquire] = useState(false);

    return (
        <div className="min-h-screen bg-[#FBFAF6] text-[#0F1115]" data-testid="family-cfo-landing">
            <header className="border-b border-[#EAE7DE] bg-white/70 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
                    <Brand />
                    <nav className="flex items-center gap-4 text-sm text-[#5E636E]">
                        <Link to="/tools/wealth-plan" className="hover:text-[#0F1115]">Wealth plan</Link>
                        <Link to="/login" className="hover:text-[#0F1115]">Sign in</Link>
                    </nav>
                </div>
            </header>

            {/* Hero */}
            <section className="mx-auto max-w-6xl px-5 pt-14 pb-10 sm:pt-20 sm:pb-16">
                <div className="grid gap-10 lg:grid-cols-[1.15fr_1fr] lg:items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#DCD8CC] bg-white px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                            <Sparkles className="h-3 w-3" /> Family CFO Plan · NZ Taxation Limited
                        </div>
                        <h1 className="mt-4 font-display text-[38px] leading-[1.05] sm:text-[52px] font-semibold tracking-tight">
                            Get a clear household money plan — <span className="text-[#0E3B2E]">and finally get ahead.</span>
                        </h1>
                        <p className="mt-5 text-[16px] sm:text-[17px] text-[#3A3D45] leading-relaxed max-w-[560px]">
                            Reduce debt, organise your living costs, build savings, and track your family&apos;s
                            financial progress month by month. Delivered by NZ Taxation Limited — not an app,
                            a guided plan with real people.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link to="/family-cfo/intake" data-testid="family-cfo-start-intake">
                                <Button className="bg-[#0E3B2E] hover:bg-[#0B1F17] text-white gap-2 rounded-full px-6 h-11">
                                    Start your Family CFO Plan <ArrowRight className="h-4 w-4" />
                                </Button>
                            </Link>
                            <Button
                                variant="outline"
                                className="border-[#DCD8CC] rounded-full px-6 h-11"
                                onClick={() => setEnquire(true)}
                                data-testid="family-cfo-enquire-btn"
                            >
                                Prefer to chat first? Enquire
                            </Button>
                        </div>
                        <div className="mt-6 text-[13px] text-[#5E636E]">
                            <b>From $59 + GST/month</b> · Cancel anytime · Prosaic bank feed included
                        </div>
                    </div>

                    <div className="rounded-3xl border border-[#EAE7DE] bg-white p-6 shadow-sm">
                        <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">What you get</div>
                        <ul className="mt-3 space-y-3 text-[14px]">
                            {[
                                "Household Financial Snapshot",
                                "Debt-Free Family Roadmap",
                                "Weekly household budget that actually works",
                                "Monthly progress review with your CFO",
                                "Emergency fund tracker",
                                "Family Financial Health Score (out of 100)",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-2.5">
                                    <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-[#0E3B2E]" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Positioning */}
            <section className="mx-auto max-w-4xl px-5 pb-14">
                <div className="rounded-2xl bg-[#0F1115] text-white p-6 sm:p-10">
                    <p className="text-[17px] sm:text-[20px] leading-relaxed font-display">
                        &ldquo;Most families don&apos;t need another budgeting app. They need a clear plan, someone to organise
                        the numbers, and a system to help them follow through.&rdquo;
                    </p>
                    <p className="mt-4 text-[13px] uppercase tracking-[0.14em] text-white/60">
                        — NZ Taxation Limited
                    </p>
                </div>
            </section>

            {/* How it works */}
            <section className="mx-auto max-w-6xl px-5 pb-14 sm:pb-24">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">How it works</h2>
                <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                        { icon: ClipboardList, title: "1. Snapshot", body: "Complete the Family CFO Financial Snapshot — income, living costs, debts, arrears, and goals." },
                        { icon: TrendingDown, title: "2. Debt-Free Roadmap", body: "We build your household budget, debt reduction plan, and 90-day action list." },
                        { icon: Wallet, title: "3. Activate", body: "Pay your first monthly fee. We send your Prosaic bank-feed invite (included in your fee)." },
                        { icon: Users, title: "4. Monthly reviews", body: "Every month we refresh your numbers, score your progress, and send a Family CFO report." },
                    ].map((step) => (
                        <div key={step.title} className="rounded-2xl border border-[#EAE7DE] bg-white p-5">
                            <div className="grid h-11 w-11 place-items-center rounded-full bg-[#0E3B2E]/10 text-[#0E3B2E]">
                                <step.icon className="h-5 w-5" />
                            </div>
                            <div className="mt-3 font-semibold">{step.title}</div>
                            <p className="mt-2 text-[13.5px] text-[#5E636E] leading-relaxed">{step.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Reassurance */}
            <section className="mx-auto max-w-4xl px-5 pb-16">
                <div className="rounded-2xl border border-[#EAE7DE] bg-white p-6 sm:p-8">
                    <div className="flex items-start gap-3">
                        <ShieldCheck className="h-6 w-6 text-[#0E3B2E] shrink-0 mt-0.5" />
                        <div>
                            <div className="font-semibold text-[15px]">This is planning, not a hardship application.</div>
                            <p className="mt-2 text-[13.5px] text-[#5E636E] leading-relaxed">
                                The Family CFO Plan provides financial organisation, budgeting, cashflow planning,
                                debt tracking, and accountability. It doesn&apos;t replace licensed legal, investment,
                                mortgage, or insurance advice — for that we&apos;ll refer you to a specialist.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="mx-auto max-w-6xl px-5 pb-24">
                <div className="rounded-3xl bg-gradient-to-br from-[#0E3B2E] to-[#0B1F17] text-white p-8 sm:p-12">
                    <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight max-w-xl">
                        Ready to see exactly where your household money is going?
                    </h3>
                    <p className="mt-3 text-white/70 max-w-xl text-[14.5px]">
                        The Financial Snapshot takes ~20 minutes. It autosaves as you go — you can pause and
                        come back anytime.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link to="/family-cfo/intake" data-testid="family-cfo-start-intake-bottom">
                            <Button className="bg-white text-[#0F1115] hover:bg-white/90 gap-2 rounded-full px-6 h-11">
                                Start Financial Snapshot <ArrowRight className="h-4 w-4" />
                            </Button>
                        </Link>
                        <Button
                            variant="outline"
                            className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 h-11"
                            onClick={() => setEnquire(true)}
                        >
                            Enquire first
                        </Button>
                    </div>
                </div>
            </section>

            <footer className="border-t border-[#EAE7DE] bg-white/50">
                <div className="mx-auto max-w-6xl px-5 py-6 text-[12px] text-[#5E636E] flex flex-wrap justify-between gap-2">
                    <div>NZ Taxation Limited t/a ClientOps · NZ Registered Tax Agent</div>
                    <div className="flex gap-4">
                        <Link to="/privacy" className="hover:text-[#0F1115]">Privacy</Link>
                        <Link to="/support" className="hover:text-[#0F1115]">Support</Link>
                    </div>
                </div>
            </footer>

            <EnquireDialog open={enquire} onClose={() => setEnquire(false)} />
        </div>
    );
}

function EnquireDialog({ open, onClose }) {
    const [form, setForm] = useState({ full_name: "", email: "", mobile: "", message: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/family-cfo/enquire", form);
            setSent(true);
            toast.success("Thanks — we'll be in touch within one business day.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setSent(false); setForm({ full_name: "", email: "", mobile: "", message: "" }); } }}>
            <DialogContent className="max-w-md" data-testid="family-cfo-enquire-dialog">
                <DialogHeader>
                    <DialogTitle>{sent ? "Enquiry received" : "Enquire about the Family CFO Plan"}</DialogTitle>
                </DialogHeader>
                {sent ? (
                    <div className="py-4 text-sm text-[#3A3D45]">
                        Thanks — a member of the NZ Taxation team will be in touch within one business day.
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <Label>Full name</Label>
                            <Input required value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} data-testid="enquire-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Email</Label>
                                <Input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="enquire-email" />
                            </div>
                            <div>
                                <Label>Mobile</Label>
                                <Input required value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} data-testid="enquire-mobile" />
                            </div>
                        </div>
                        <div>
                            <Label>Message (optional)</Label>
                            <Textarea rows={3} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} data-testid="enquire-message" placeholder="Briefly, what's on your mind?" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
                            <Button type="submit" disabled={sending} className="bg-[#0E3B2E] hover:bg-[#0B1F17] text-white gap-2" data-testid="enquire-submit">
                                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                {sending ? "Sending…" : "Send enquiry"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
